import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { ArrowLeft, DollarSign, FileText, Users, XCircle, Clock, Shield, AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import RevenueActionPanel from '../components/revenue/RevenueActionPanel';

const STATUS_COLORS = {
  ready_for_patient_responsibility: 'bg-emerald-600', small_balance_accumulation: 'bg-amber-500',
  pending_primary: 'bg-slate-400', pending_secondary: 'bg-slate-400',
  pending_appeal_or_dispute: 'bg-red-500', hold_special_circumstance: 'bg-orange-500', resolved: 'bg-gray-400',
  ready_for_statement_review: 'bg-emerald-600', approved_for_statement_generation: 'bg-blue-600',
  ready_for_collections_review: 'bg-emerald-600', approved_for_collections: 'bg-blue-600',
  ready_for_writeoff_review: 'bg-emerald-600', approved_for_writeoff_execution: 'bg-blue-600',
  manually_deferred: 'bg-amber-500', manually_suppressed: 'bg-gray-400',
  not_applicable: 'bg-gray-300', active: 'bg-emerald-600', paused: 'bg-amber-500', closed: 'bg-gray-400',
  handoff_complete: 'bg-blue-600', handoff_failed: 'bg-red-500',
};

function formatCents(c) { return c != null ? `$${(c / 100).toFixed(2)}` : '—'; }
function formatDate(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
function StatusDot({ status }) { const c = STATUS_COLORS[status] || 'bg-gray-300'; return <span className={`inline-block w-2 h-2 rounded-full ${c}`} />; }
function StatusText({ status }) { return status ? <span className="text-xs font-medium">{(status || '').replace(/_/g, ' ')}</span> : <span className="text-xs text-muted-foreground">—</span>; }

function FlagRow({ label, active }) {
  if (!active) return null;
  return <div className="flex items-center gap-1.5 text-xs"><AlertTriangle className="h-3 w-3 text-amber-500" /><span>{label}</span></div>;
}

function SectionCard({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="py-2.5 px-4 cursor-pointer hover:bg-muted/20 transition-colors">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              {Icon && <Icon className="h-4 w-4" />}{title}
              <span className="ml-auto">{open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent><CardContent className="pt-0 pb-3 px-4">{children}</CardContent></CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function InfoRow({ label, value }) {
  return <div className="flex justify-between items-center py-1 text-xs"><span className="text-muted-foreground">{label}</span><span className="font-medium text-right">{value ?? '—'}</span></div>;
}

function TimelineEvent({ event }) {
  const type = event.trigger_type || event.action_type || '';
  return (
    <div className="flex gap-3 py-2 border-b last:border-0">
      <div className="flex flex-col items-center pt-1"><div className="w-2 h-2 rounded-full bg-slate-300 shrink-0" /><div className="w-px flex-1 bg-slate-200" /></div>
      <div className="flex-1 min-w-0 pb-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium truncate">{type.replace(/_/g, ' ')}</span>
          <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(event.created_at)}</span>
        </div>
        {event.decision_trace && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{event.decision_trace}</p>}
        <div className="flex items-center gap-2 mt-0.5">
          {event.previous_state && <span className="text-[10px] text-muted-foreground">{event.previous_state.replace(/_/g, ' ')}</span>}
          {event.previous_state && event.new_state && <span className="text-[10px]">→</span>}
          {event.new_state && <span className="text-[10px] font-medium">{event.new_state.replace(/_/g, ' ')}</span>}
        </div>
        {event.actor_id && event.actor_id !== 'system' && <span className="text-[10px] text-muted-foreground">by {event.actor_id.slice(0, 8)}...</span>}
      </div>
    </div>
  );
}

export default function RevenueCaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [events, setEvents] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [collAction, setCollAction] = useState(null);
  const [woAction, setWoAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [caseRes] = await Promise.all([api.get(`/revenue/responsibility/${caseId}`)]);
      setCaseData(caseRes.data.case);
      setEvents(caseRes.data.events || []);
      // Load artifacts, dispatches, collections, writeoff in parallel (ignore 404s)
      const [artRes, collRes, woRes] = await Promise.allSettled([
        api.get(`/revenue/statements/${caseId}/artifacts`),
        api.get(`/revenue/collections/${caseId}/execution-status`),
        api.get(`/revenue/writeoff/${caseId}/execution-status`),
      ]);
      if (artRes.status === 'fulfilled') {
        const arts = artRes.value.data.artifacts || [];
        setArtifacts(arts);
        const currentArt = arts.find(a => a.is_current);
        if (currentArt) {
          try { const dRes = await api.get(`/revenue/statements/artifacts/${currentArt.id}/dispatch-history`); setDispatches(dRes.data.records || []); } catch {}
        }
      }
      if (collRes.status === 'fulfilled') setCollAction(collRes.value.data.action);
      if (woRes.status === 'fulfilled') setWoAction(woRes.value.data.action);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load case.'); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="flex-1 p-4 space-y-3" data-testid="case-loading"><Skeleton className="h-8 w-48" /><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (error) return <div className="flex-1 p-4 text-center py-12" data-testid="case-error"><AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-400" /><p className="text-sm text-muted-foreground mb-2">{error}</p><Button size="sm" onClick={() => navigate('/revenue')}>Back to Queues</Button></div>;
  if (!caseData) return <div className="flex-1 p-4 text-center py-12" data-testid="case-not-found"><p className="text-sm">Case not found.</p><Button size="sm" onClick={() => navigate('/revenue')} className="mt-2">Back</Button></div>;

  const c = caseData;
  const currentArt = artifacts.find(a => a.is_current);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-5xl" data-testid="revenue-case-detail">
      {/* Breadcrumb — preserves queue context */}
      <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => navigate(-1)} data-testid="back-to-queues">
        <ArrowLeft className="h-3 w-3" />Back
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between" data-testid="case-header">
        <div>
          <h1 className="text-lg font-semibold">Case: {c.id?.slice(0, 12)}...</h1>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span>Patient: {c.patient_id?.slice(0, 16)}</span>
            <span>Encounter: {c.encounter_id?.slice(0, 16)}</span>
            {c.guarantor_id && <span>Guarantor: {c.guarantor_id.slice(0, 16)}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold font-mono">{formatCents(c.balance_amount_cents)}</div>
          <div className="text-xs text-muted-foreground">{c.aging_days || 0} days aging</div>
        </div>
      </div>

      {/* Flags */}
      <div className="flex flex-wrap gap-3">
        <FlagRow label="Manual Hold Active" active={c.manual_hold_active} />
        <FlagRow label="Manual Suppress Active" active={c.manual_suppress_active} />
        <FlagRow label="Hardship Flag" active={c.hardship_flag} />
        <FlagRow label="Dispute Flag" active={c.dispute_flag} />
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="status-cards">
        <Card className="border p-3">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-xs font-medium">Responsibility</span></div>
          <div className="flex items-center gap-1.5"><StatusDot status={c.state} /><StatusText status={c.state} /></div>
          <div className="text-[10px] text-muted-foreground mt-1">Queue: {c.effective_queue || 'none'}</div>
        </Card>
        <Card className="border p-3">
          <div className="flex items-center gap-2 mb-1"><FileText className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-xs font-medium">Statement</span></div>
          <div className="flex items-center gap-1.5"><StatusDot status={c.statement_readiness_status} /><StatusText status={c.statement_readiness_status} /></div>
          {currentArt && <div className="text-[10px] text-muted-foreground mt-1">Artifact v{currentArt.artifact_version}</div>}
        </Card>
        <Card className="border p-3">
          <div className="flex items-center gap-2 mb-1"><Users className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-xs font-medium">Collections</span></div>
          <div className="flex items-center gap-1.5"><StatusDot status={c.collections_readiness_status} /><StatusText status={c.collections_readiness_status} /></div>
          {collAction && <div className="text-[10px] text-muted-foreground mt-1">{collAction.action_type}: {collAction.execution_status}</div>}
        </Card>
        <Card className="border p-3">
          <div className="flex items-center gap-2 mb-1"><XCircle className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-xs font-medium">Write-Off</span></div>
          <div className="flex items-center gap-1.5"><StatusDot status={c.writeoff_readiness_status} /><StatusText status={c.writeoff_readiness_status} /></div>
          {woAction && <div className="text-[10px] text-muted-foreground mt-1">{woAction.execution_status}</div>}
        </Card>
      </div>

      {/* Explanation */}
      {c.last_reason && (
        <Card className="border bg-muted/20 p-3">
          <div className="text-xs"><span className="font-medium">Current reason: </span>{c.last_reason}</div>
          {c.statement_readiness_reason && c.statement_readiness_reason !== c.last_reason && <div className="text-xs mt-1"><span className="font-medium">Statement: </span>{c.statement_readiness_reason}</div>}
          {c.collections_readiness_reason && <div className="text-xs mt-1"><span className="font-medium">Collections: </span>{c.collections_readiness_reason}</div>}
          {c.writeoff_readiness_reason && <div className="text-xs mt-1"><span className="font-medium">Write-off: </span>{c.writeoff_readiness_reason}</div>}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Timeline */}
        <SectionCard title="Event Timeline" icon={Clock}>
          {events.length === 0 ? <p className="text-xs text-muted-foreground">No events recorded.</p> : (
            <div className="max-h-96 overflow-y-auto" data-testid="event-timeline">
              {events.slice(0, 30).map((e, i) => <TimelineEvent key={e.id || i} event={e} />)}
              {events.length > 30 && <p className="text-xs text-muted-foreground text-center py-2">{events.length - 30} more events...</p>}
            </div>
          )}
        </SectionCard>

        {/* Statements */}
        <SectionCard title="Statement Artifacts" icon={FileText}>
          {artifacts.length === 0 ? <p className="text-xs text-muted-foreground">No statement artifacts.</p> : (
            <div className="space-y-2" data-testid="artifacts-section">
              {artifacts.map(a => (
                <div key={a.id} className={`p-2 rounded border text-xs ${a.is_current ? 'bg-blue-50 border-blue-200' : 'bg-muted/20'}`}>
                  <div className="flex justify-between"><span className="font-medium">v{a.artifact_version} — {a.artifact_status?.replace(/_/g, ' ')}</span>{a.is_current && <Badge variant="outline" className="text-[10px] h-4">Current</Badge>}</div>
                  <div className="text-muted-foreground mt-0.5">Balance: {formatCents(a.totals?.patient_responsibility_cents)} | {formatDate(a.generated_at)}</div>
                </div>
              ))}
            </div>
          )}
          {dispatches.length > 0 && (
            <div className="mt-3 pt-2 border-t" data-testid="dispatch-section">
              <div className="text-xs font-medium mb-1">Dispatch History</div>
              {dispatches.map(d => (
                <div key={d.id} className="text-xs py-1 flex justify-between">
                  <span>{d.channel} → {d.destination?.slice(0, 20)} — <span className={d.voided ? 'text-red-500' : 'text-emerald-600'}>{d.voided ? 'voided' : d.status}</span></span>
                  <span className="text-muted-foreground">{formatDate(d.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Collections */}
        <SectionCard title="Collections" icon={Users} defaultOpen={!!collAction}>
          {!collAction ? <p className="text-xs text-muted-foreground">No active collections action.</p> : (
            <div className="space-y-1" data-testid="collections-section">
              <InfoRow label="Action Type" value={collAction.action_type?.replace(/_/g, ' ')} />
              <InfoRow label="Status" value={collAction.execution_status?.replace(/_/g, ' ')} />
              <InfoRow label="Started" value={formatDate(collAction.started_at)} />
              <InfoRow label="Balance Snapshot" value={formatCents(collAction.balance_snapshot_cents)} />
              {collAction.agency_vendor && <InfoRow label="Agency" value={collAction.agency_vendor} />}
              {collAction.agency_reference && <InfoRow label="Reference" value={collAction.agency_reference} />}
              {collAction.assigned_to && <InfoRow label="Assigned To" value={collAction.assigned_to} />}
              {collAction.followup_count > 0 && <InfoRow label="Follow-ups" value={collAction.followup_count} />}
              {collAction.closure_reason && <InfoRow label="Closure" value={collAction.closure_reason} />}
            </div>
          )}
        </SectionCard>

        {/* Write-Off */}
        <SectionCard title="Write-Off" icon={XCircle} defaultOpen={!!woAction}>
          {!woAction ? <p className="text-xs text-muted-foreground">No active write-off action.</p> : (
            <div className="space-y-1" data-testid="writeoff-section">
              <InfoRow label="Status" value={woAction.execution_status?.replace(/_/g, ' ')} />
              <InfoRow label="Amount" value={formatCents(woAction.writeoff_amount_cents)} />
              <InfoRow label="Reason Code" value={woAction.writeoff_reason_code} />
              <InfoRow label="Started" value={formatDate(woAction.started_at)} />
              {woAction.closure_reason && <InfoRow label="Closure" value={woAction.closure_reason} />}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Action Panel */}
      <Card className="border p-4" data-testid="action-panel-card">
        <h3 className="text-sm font-semibold mb-3">Actions</h3>
        <RevenueActionPanel caseData={{ ...c, _collAction: collAction, _woAction: woAction }} onRefresh={fetchAll} />
      </Card>

      {/* Refresh */}
      <div className="text-center pt-2">
        <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={fetchAll} data-testid="refresh-case">
          <RefreshCw className="h-3 w-3" />Refresh
        </Button>
      </div>
    </div>
  );
}
